<script setup lang="ts">
import type { VNode } from "vue";
import type { TabItemRef } from "../types";
import { useMutationObserver } from "@vueuse/core";
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, toRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import VBottomSheet from "../../BottomSheet/VBottomSheet.vue";
import VIcon from "../../Icon/VIcon.vue";
import MobileSheetGroup from "../../MobileSheet/MobileSheetGroup.vue";
import MobileSheetRow from "../../MobileSheet/MobileSheetRow.vue";
import { TAB_BAR_OVERFLOW, TAB_LIST_BAR } from "../tabBar";
import { useTabsState } from "../useTabsState";
import { tabListHasOverflow, tabListScrollBehavior, tabListScrollEdges, tabListWheelScroll } from "./tabListScroll";
import { tabListActiveInkbarCva, tabListContainerCva, tabListContentWrapperCva, tabListDividerCva, tabListInkbarBackgroundCva, tabListItemCva, tabListItemsContainerCva, tabListNextButtonCva, tabListPreviousButtonCva } from "./VTabList.cva";

const { bar = false } = defineProps<{
  /** Renders the items as a bottom tab bar: icon over a small label, spread over the full width. */
  bar?: boolean
}>();

defineSlots<{
  "default"?: () => VNode[]
  "prev-icon"?: () => VNode[]
  "next-icon"?: () => VNode[]
  "right"?: () => VNode[]
}>();

provide(TAB_LIST_BAR, toRef(() => bar));

const tabsContainer = ref<HTMLElement | null>(null);
const inkbarRef = ref<HTMLElement | null>(null);
const itemsContainerRef = ref<HTMLElement | null>(null);
const itemsRef = ref<HTMLElement | null>(null);

const showPrevButton = ref(false);
const showNextButton = ref(false);
const shouldShowButtons = ref(false);

const {
  activeIndex,
  orientation,
  listPosition,
  tile,
  scrollable,
  tabItems,
  inkbar,
  centerItemsInList,
  dividerBetweenTabs,
  appearance,
  onTabClick,
} = useTabsState();

const isHeader = computed(() => appearance?.value === "header" && orientation?.value === "horizontal" && !tile?.value);
// A default horizontal bar draws its line as the list's bottom border; a track on top of it would double the line.
const hasInkbarTrack = computed(() => orientation?.value === "vertical" || isHeader.value);

// Bar mode: like a phone tab bar, at most five buttons. With more tabs the first four stay and the rest
// move under a "more" button that opens a bottom sheet. Tabs hidden with v-show do not count.
const MAX_BAR_ITEMS = 5;
const { t, te } = useI18n();
const moreLabel = computed(() => te("navbar.more") ? t("navbar.more") : "More");
const barEl = ref<HTMLElement | null>(null);
const barVersion = ref(0);
useMutationObserver(barEl, () => {
  barVersion.value++;
}, { subtree: true, childList: true, attributes: true, attributeFilter: ["style"] });

const visibleBarTabs = computed<TabItemRef[]>(() => {
  void barVersion.value;
  return tabItems.value
    .filter(tab => tab.el && tab.el.style.display !== "none")
    .sort((a, b) => (a.el!.compareDocumentPosition(b.el!) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1);
});
const overflowTabs = computed(() => visibleBarTabs.value.length > MAX_BAR_ITEMS
  ? visibleBarTabs.value.slice(MAX_BAR_ITEMS - 1)
  : []);
const overflowIndexes = computed(() => new Set(overflowTabs.value.map(tab => tab.index)));
provide(TAB_BAR_OVERFLOW, overflowIndexes);

const isMoreOpen = ref(false);
const isOverflowActive = computed(() => overflowIndexes.value.has(activeIndex.value));

function selectOverflowTab(tab: TabItemRef) {
  if (tab.disabled) {
    return;
  }
  onTabClick(tab.index);
  isMoreOpen.value = false;
}

const previousLabel = computed(() => t("base.previous"));
const nextLabel = computed(() => t("base.next"));

function isTabItemNode(node: VNode | undefined): boolean {
  const type = node?.type;
  return typeof type === "object" && type !== null && "__name" in type && type.__name === "VTabItem";
}

const activeTabError = computed(() => {
  const activeTab = tabItems.value.find(tab => tab.index === activeIndex.value);
  return Boolean(activeTab?.error || activeTab?.danger);
});

function getScrollContainer() {
  return itemsContainerRef.value;
}

function scrollMetrics(container: HTMLElement, scrollPosition?: number) {
  const isVertical = orientation?.value === "vertical";
  return {
    orientation: isVertical ? "vertical" as const : "horizontal" as const,
    scrollPosition: scrollPosition ?? (isVertical ? container.scrollTop : container.scrollLeft),
    scrollSize: isVertical ? container.scrollHeight : container.scrollWidth,
    clientSize: isVertical ? container.clientHeight : container.clientWidth,
    rtl: !isVertical && getComputedStyle(container).direction === "rtl",
  };
}

function checkOverflow() {
  const container = getScrollContainer();
  if (!container || !tabsContainer.value) {
    syncWheelListener(false);
    return;
  }

  const metrics = scrollMetrics(container);
  const hasOverflow = tabListHasOverflow(metrics);
  syncWheelListener(hasOverflow && metrics.orientation === "horizontal");

  shouldShowButtons.value = hasOverflow && !scrollable?.value;

  if (shouldShowButtons.value) {
    const edges = tabListScrollEdges(metrics);
    showPrevButton.value = edges.canScrollBack;
    showNextButton.value = edges.canScrollForward;
  } else {
    showPrevButton.value = false;
    showNextButton.value = false;
  }
}

// A vertical mouse wheel over an overflowing horizontal strip scrolls the strip. The listener must be non-passive
// to cancel the page scroll, so it is only attached while the strip actually overflows horizontally.
const WHEEL_TARGET_TTL_MS = 400;
let wheelListenerElement: HTMLElement | null = null;
// Target of a smooth wheel scroll still in progress, so quick wheel turns add up instead of restarting.
let wheelTarget: number | null = null;
let wheelTargetAt = 0;

function syncWheelListener(enabled: boolean) {
  const container = enabled ? getScrollContainer() : null;
  if (wheelListenerElement === container) {
    return;
  }
  wheelListenerElement?.removeEventListener("wheel", onWheel);
  wheelListenerElement = container;
  wheelTarget = null;
  container?.addEventListener("wheel", onWheel, { passive: false });
}

function onWheel(event: WheelEvent) {
  const container = getScrollContainer();
  if (!container) {
    return;
  }

  const now = performance.now();
  const pendingTarget = wheelTarget !== null && now - wheelTargetAt < WHEEL_TARGET_TTL_MS ? wheelTarget : null;
  const from = pendingTarget ?? container.scrollLeft;
  const result = tabListWheelScroll(event, scrollMetrics(container, from));
  if (!result) {
    return;
  }

  event.preventDefault();
  const behavior = tabListScrollBehavior(result.smooth);
  const left = from + result.left;
  wheelTarget = behavior === "smooth" ? left : null;
  wheelTargetAt = now;
  container.scrollTo({ left, behavior });
}

function updateInkBar() {
  if (!tabsContainer.value || !inkbarRef.value || !inkbar?.value) {
    return;
  }

  const activeTab = tabsContainer.value.querySelector(".tab-item[aria-selected='true']");
  if (!activeTab) {
    return;
  }

  const items = itemsRef.value;
  if (!items) {
    return;
  }

  // The inkbar lives inside the tab wrapper, which scrolls with the tabs, so no scroll offset is needed.
  const activeTabRect = (activeTab as HTMLElement).getBoundingClientRect();
  const itemsRect = items.getBoundingClientRect();

  const isVertical = orientation?.value === "vertical";

  const pos = isVertical
    ? activeTabRect.top - itemsRect.top
    : activeTabRect.left - itemsRect.left;

  const size = isVertical ? activeTabRect.height : activeTabRect.width;

  const styles = isVertical
    ? {
        top: `${pos}px`,
        height: `${size}px`,
        width: "2px",
        ...(tile?.value
          ? {
              [listPosition?.value === "left" ? "right" : "left"]: "0",
              borderRadius: "0",
            }
          : {
              [listPosition?.value === "left" ? "right" : "left"]: "0",
              borderRadius: undefined,
            }),
      }
    : {
        left: `${pos}px`,
        width: `${size}px`,
        height: "2px",
        bottom: "0",
      };

  inkbarRef.value.style.cssText = Object.entries(styles)
    .filter(([,value]) => value !== undefined)
    .map(([key, value]) => `${key}: ${value}`)
    .join(";");
}

function scroll(direction: 1 | -1) {
  const container = getScrollContainer();
  if (!container) {
    return;
  }

  const isVertical = orientation?.value === "vertical";
  const amount = isVertical ? container.clientHeight : container.clientWidth;
  const prop = isVertical ? "top" : "left";

  wheelTarget = null;
  container.scrollBy({
    [prop]: amount * direction,
    behavior: tabListScrollBehavior(),
  });
}

function scrollPrev() {
  scroll(-1);
}

function scrollNext() {
  scroll(1);
}

/** Brings the active tab into view inside the rail only, without scrolling the page or dialog around it. */
function scrollActiveIntoView() {
  const container = getScrollContainer();
  const activeTab = tabsContainer.value?.querySelector<HTMLElement>(".tab-item[aria-selected='true']");
  if (!container || !activeTab) {
    return;
  }

  const isVertical = orientation?.value === "vertical";
  const tabRect = activeTab.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const before = isVertical ? tabRect.top - containerRect.top : tabRect.left - containerRect.left;
  const after = isVertical ? tabRect.bottom - containerRect.bottom : tabRect.right - containerRect.right;
  const delta = before < 0 ? before : Math.max(after, 0);
  if (delta === 0) {
    return;
  }

  wheelTarget = null;
  container.scrollBy({ [isVertical ? "top" : "left"]: delta, behavior: tabListScrollBehavior() });
}

watch(activeIndex, () => {
  nextTick(() => {
    updateInkBar();
    scrollActiveIntoView();
  });
});

function onScroll() {
  checkOverflow();
}

watch(() => orientation?.value, () => {
  nextTick(checkOverflow);
});

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  nextTick(() => {
    checkOverflow();
    updateInkBar();
    scrollActiveIntoView();
  });

  const container = getScrollContainer();
  if (!container) {
    return;
  }

  resizeObserver = new ResizeObserver(() => {
    checkOverflow();
    updateInkBar();
  });
  resizeObserver.observe(container);
  if (container.parentElement) {
    resizeObserver.observe(container.parentElement);
  }
  // Tabs that appear, disappear or change their label change the overflow without resizing the rail.
  if (itemsRef.value) {
    resizeObserver.observe(itemsRef.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  syncWheelListener(false);
});

const containerClasses = computed(() => {
  return tabListContainerCva({
    orientation: orientation?.value,
    listPosition: listPosition?.value,
    tile: tile?.value,
    header: isHeader.value,
  });
});

const previousButtonClasses = computed(() => {
  return tabListPreviousButtonCva({
    orientation: orientation?.value,
    listPosition: listPosition?.value,
  });
});

const nextButtonClasses = computed(() => {
  return tabListNextButtonCva({
    orientation: orientation?.value,
    listPosition: listPosition?.value,
  });
});

const inkbarBackgroundClasses = computed(() => {
  return tabListInkbarBackgroundCva({
    orientation: orientation?.value,
    listPosition: listPosition?.value,
  });
});

const activeInkbarClasses = computed(() => {
  return tabListActiveInkbarCva({
    orientation: orientation?.value,
    error: activeTabError.value,
    tile: tile?.value,
  });
});

const contentWrapperClasses = computed(() => {
  return tabListContentWrapperCva({
    orientation: orientation?.value,
    buttons: shouldShowButtons.value,
    header: isHeader.value,
  });
});

const itemClasses = computed(() => {
  return tabListItemCva({
    orientation: orientation?.value,
    tile: tile?.value,
    center: centerItemsInList?.value,
    header: isHeader.value,
  });
});

const dividerClasses = computed(() => {
  return tabListDividerCva({
    orientation: orientation?.value,
    listPosition: listPosition?.value,
  });
});

const itemsContainerClasses = computed(() => {
  return tabListItemsContainerCva({
    orientation: orientation?.value,
    tile: tile?.value,
    divider: dividerBetweenTabs?.value,
    center: centerItemsInList?.value,
    scrollable: scrollable?.value,
    header: isHeader.value,
  });
});
</script>

<template>
  <div
    v-if="bar"
    ref="barEl"
    class="flex w-full"
    role="tablist"
  >
    <slot />
    <button
      v-if="overflowTabs.length"
      type="button"
      class="relative flex flex-1 min-w-16 h-16 flex-col items-center justify-center gap-1 px-1 transition-colors"
      :class="isOverflowActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'"
      aria-haspopup="dialog"
      :aria-expanded="isMoreOpen"
      @click="isMoreOpen = true"
    >
      <span
        class="relative flex items-center justify-center h-7 w-12 rounded-full transition-colors duration-200"
        :class="isOverflowActive && 'bg-primary-50 dark:bg-primary-900/30'"
      >
        <VIcon name="ellipsis" class="text-[17px]" />
        <span v-if="overflowTabs.some(tab => tab.error)" class="absolute right-2 top-0.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-dark-800" />
      </span>
      <span class="max-w-full truncate text-[11px] leading-none" :class="isOverflowActive && 'font-semibold'">
        {{ moreLabel }}
      </span>
    </button>
    <VBottomSheet v-model:open="isMoreOpen" :title="moreLabel">
      <MobileSheetGroup>
        <MobileSheetRow
          v-for="tab in overflowTabs"
          :key="tab.index"
          :icon="tab.icon"
          :label="tab.title ?? ''"
          :class="tab.disabled && 'opacity-40'"
          :aria-disabled="tab.disabled || undefined"
          @click="selectOverflowTab(tab)"
        >
          <template #trailing>
            <VIcon v-if="tab.index === activeIndex" name="check" class="text-primary-500" />
          </template>
        </MobileSheetRow>
      </MobileSheetGroup>
    </VBottomSheet>
  </div>
  <div
    v-else
    ref="tabsContainer"
    :class="[containerClasses]"
    role="tablist"
  >
    <!-- Previous button -->
    <button
      v-if="shouldShowButtons"
      type="button"
      :disabled="!showPrevButton"
      :aria-label="previousLabel"
      :class="[previousButtonClasses]"
      @click.prevent="scrollPrev"
      @mousedown.stop
    >
      <slot name="prev-icon">
        <VIcon :name="orientation === 'horizontal' ? 'chevron-left' : 'chevron-up'" aria-hidden="true" />
      </slot>
    </button>

    <!-- Tab content wrapper -->
    <div
      :class="[contentWrapperClasses]"
    >
      <!-- Tab items container -->
      <div
        ref="itemsContainerRef"
        :class="[itemsContainerClasses]"
        @scroll="onScroll"
      >
        <!-- Tab items -->
        <div
          ref="itemsRef"
          :class="[itemClasses]"
        >
          <template v-for="(item, index) in $slots.default?.() || []" :key="index">
            <component :is="item" />
            <div
              v-if="dividerBetweenTabs
                && index < ($slots.default?.()?.length || 0) - 1
                && isTabItemNode(item)
                && isTabItemNode($slots.default?.()[index + 1])"
              :class="[dividerClasses]"
            />
          </template>

          <!-- Inkbar background -->
          <div
            v-if="inkbar && hasInkbarTrack"
            :class="[inkbarBackgroundClasses]"
          />

          <!-- Active inkbar -->
          <div
            v-if="inkbar"
            ref="inkbarRef"
            :class="[activeInkbarClasses]"
          />
        </div>
      </div>
    </div>

    <!-- Next button -->
    <button
      v-if="shouldShowButtons"
      type="button"
      :disabled="!showNextButton"
      :aria-label="nextLabel"
      :class="[nextButtonClasses]"
      @click.prevent="scrollNext"
      @mousedown.stop
    >
      <slot name="next-icon">
        <VIcon :name="orientation === 'horizontal' ? 'chevron-right' : 'chevron-down'" aria-hidden="true" />
      </slot>
    </button>

    <!--
      Right slot: rendered alongside the tab list (right side for horizontal,
      bottom for vertical). Useful for actions like a search input or button.
    -->
    <div
      v-if="$slots.right"
      class="flex shrink-0"
      :class="[
        orientation === 'horizontal'
          ? 'items-center px-2 ml-auto'
          : 'items-stretch px-2 mt-auto',
      ]"
    >
      <slot name="right" />
    </div>
  </div>
</template>
